import { useEffect, useState, type ReactNode } from 'react'
import {
  AppShell,
  Avatar,
  Burger,
  Divider,
  Group,
  Menu,
  NavLink,
  ScrollArea,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import {
  IconBell,
  IconBrandTelegram,
  IconBuildingStore,
  IconCake,
  IconChartBar,
  IconCategory,
  IconChecklist,
  IconHistory,
  IconFilterStar,
  IconLayoutKanban,
  IconKey,
  IconLogout,
  IconReportAnalytics,
  IconSettings,
  IconShieldLock,
  IconSnowflake,
  IconUsers,
} from '@tabler/icons-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from '../auth/store'
import { logout } from '../../shared/api/auth'
import { fetchDepartments, type DepartmentRow } from '../../shared/api/departments'
import { fetchMyOpenTasks } from '../../shared/api/tasks'
import { fetchUpcomingBirthdays } from '../../shared/api/project-crm'
import { TgLinkBanner } from '../profile/tg-link-banner'
import { NotificationBell } from '../notifications/notification-bell'
import { GlobalSearch } from '../search/global-search'
import { useQuery } from '@tanstack/react-query'

export function AppLayout({ children }: { children: ReactNode }) {
  const [opened, { toggle }] = useDisclosure()
  const { user, setUser } = useAuthStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [departments, setDepartments] = useState<DepartmentRow[]>([])
  // Epic B: счётчик ДР в ближайшие 7 дней для бейджа в sidebar
  const birthdays7 = useQuery({
    queryKey: ['birthdays', '7'],
    queryFn: () => fetchUpcomingBirthdays(7),
    enabled: !!user && user.role !== 'observer',
  })
  // «Моя работа»: красный бейдж = просроченные; queryKey общий со страницей /my
  const myTasks = useQuery({
    queryKey: ['my-tasks', user?.id],
    queryFn: () => fetchMyOpenTasks(user!.id),
    enabled: !!user,
  })
  const myOverdue = myTasks.data?.filter((t) => t.due_state === 'overdue').length ?? 0

  // O6 — список отделов в боковом меню (для admin/director — все, head — свой)
  useEffect(() => {
    if (!user) return
    if (!['admin', 'director', 'head'].includes(user.role)) return
    fetchDepartments()
      .then(setDepartments)
      .catch(() =>
        notifications.show({
          color: 'red',
          title: 'Ошибка',
          message: 'Не удалось загрузить список отделов для меню',
        }),
      )
  }, [user])

  const visibleDepts =
    user?.role === 'head'
      ? departments.filter((d) => user.departments.some((ud) => ud.id === d.id))
      : departments
  const currentDept = new URLSearchParams(location.search).get('department')
  // Sidebar сохраняет контекст: клик по отделу — на текущей странице (/projects или /admin/users),
  // иначе по умолчанию /projects (главная листинг-страница).
  const sidebarBase = location.pathname.startsWith('/admin/users') ? '/admin/users' : '/projects'

  async function onLogout() {
    await logout()
    setUser(null)
    navigate('/login', { replace: true })
  }

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding={0}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Group gap="xs">
              <IconSnowflake size={22} color="var(--mantine-color-blue-8)" />
              <Title order={4} c="blue.9" fw={700}>
                Ledokol CRM
              </Title>
            </Group>
          </Group>

          <Group gap="sm">
          <GlobalSearch />
          <NotificationBell />
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <UnstyledButton>
                <Group gap="xs">
                  <Avatar name={user?.full_name} color="blue" size={32} radius="xl">
                    {user?.full_name?.[0] ?? '?'}
                  </Avatar>
                  <div style={{ textAlign: 'right' }}>
                    <Text size="sm" fw={600}>
                      {user?.full_name ?? '—'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {user?.departments.map((d) => d.name).join(', ') || roleLabel(user?.role)}
                    </Text>
                  </div>
                </Group>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>{user?.email}</Menu.Label>
              {/* T12 эпик P4 — вход в /me/telegram через аватар-меню для УЖЕ привязанных
                  (переподключить); для непривязанных основной вход — баннер выше Outlet.
                  Label меняется в зависимости от состояния, оба пункта ведут на /me/telegram. */}
              <Menu.Item
                component={Link}
                to="/me/telegram"
                leftSection={<IconBrandTelegram size={16} stroke={1.6} />}
              >
                {user?.tg_linked_at
                  ? `Мой Telegram (@${user.tg_username ?? '…'})`
                  : 'Привязать Telegram'}
              </Menu.Item>
              {user?.has_password && (
                <Menu.Item component={Link} to="/me/password" leftSection={<IconKey size={16} />}>
                  Сменить пароль
                </Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item leftSection={<IconLogout size={16} />} onClick={onLogout} c="red">
                Выйти
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <AppShell.Section grow component={ScrollArea}>
          <NavLink
            component={Link}
            to="/my"
            label="Моя работа"
            description={
              myTasks.data ? `${myTasks.data.length} ${openTasksWord(myTasks.data.length)}` : 'Мои задачи'
            }
            leftSection={<IconChecklist size={18} stroke={1.6} />}
            active={location.pathname === '/my'}
            rightSection={
              myOverdue > 0 ? (
                <span style={{
                  background: '#fa5252', color: 'white', borderRadius: 10, fontSize: 11,
                  padding: '2px 8px', fontWeight: 700,
                }}>{myOverdue}</span>
              ) : null
            }
          />
          <NavLink
            component={Link}
            to="/projects"
            label="Рекламодатели"
            description="Все проекты"
            leftSection={<IconBuildingStore size={18} stroke={1.6} />}
            active={location.pathname === '/projects'}
          />
          <NavLink
            label="Канбан-доска"
            description="Выберите проект слева"
            leftSection={<IconLayoutKanban size={18} stroke={1.6} />}
            disabled
          />
          {user && ['admin', 'director', 'head'].includes(user.role) && (
            <NavLink
              component={Link}
              to="/dashboard"
              label="Дашборд"
              description="KPI отдела"
              leftSection={<IconChartBar size={18} stroke={1.6} />}
              active={location.pathname === '/dashboard'}
            />
          )}
          {user && user.role !== 'observer' && (
            <NavLink
              component={Link}
              to={`/reports/employee/${user.id}`}
              label="Мой отчёт"
              description="Моя статистика"
              leftSection={<IconReportAnalytics size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/reports/employee')}
            />
          )}
          {user && ['admin', 'director', 'head', 'account'].includes(user.role) && (
            <NavLink
              component={Link}
              to="/settings"
              label="Настройки"
              description="Регулярки, праздники, шаблоны"
              leftSection={<IconSettings size={18} stroke={1.6} />}
              active={location.pathname === '/settings'}
            />
          )}
          {/* Epic B: глобальные ДР с бейджем-счётчиком на 7 дней */}
          {user && user.role !== 'observer' && (
            <NavLink
              component={Link}
              to="/birthdays"
              label="Ближайшие ДР"
              description={
                birthdays7.data && birthdays7.data.length > 0
                  ? `${birthdays7.data.length} в ближайшие 7 дней`
                  : 'У контактов клиентов'
              }
              leftSection={<IconCake size={18} stroke={1.6} />}
              active={location.pathname === '/birthdays'}
              rightSection={
                birthdays7.data && birthdays7.data.length > 0 ? (
                  <span style={{
                    background: '#fa5252', color: 'white', borderRadius: 10, fontSize: 11,
                    padding: '2px 8px', fontWeight: 700,
                  }}>{birthdays7.data.length}</span>
                ) : null
              }
            />
          )}
          {user && user.role === 'admin' && (
            <NavLink
              component={Link}
              to="/admin/users"
              label="Сотрудники"
              description="Управление + промоут стабов"
              leftSection={<IconUsers size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/admin/users')}
            />
          )}
          {user && user.role === 'admin' && (
            <NavLink
              component={Link}
              to="/admin/departments"
              label="Отделы"
              description="Направления агентства"
              leftSection={<IconCategory size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/admin/departments')}
            />
          )}
          {user && user.role === 'admin' && (
            <NavLink
              component={Link}
              to="/admin/segments"
              label="Сегменты воронки"
              description="Справочник для рассылок"
              leftSection={<IconFilterStar size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/admin/segments')}
            />
          )}
          {user && user.role === 'admin' && (
            <NavLink
              component={Link}
              to="/admin/tg-bindings"
              label="Чаты Telegram"
              description="Привязки + orphan"
              leftSection={<IconBrandTelegram size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/admin/tg-bindings')}
            />
          )}
          {/* #5 (2E): анти-спам — гейт по ПРАВУ tg_notifications.manage (делегируется не-админу). */}
          {user && user.permissions.includes('tg_notifications.manage') && (
            <NavLink
              component={Link}
              to="/admin/tg-notifications"
              label="Уведомления в Telegram"
              description="Окно тишины, дайджест, рассылка"
              leftSection={<IconBell size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/admin/tg-notifications')}
            />
          )}
          {/* #7 слой 3: журнал действий — гейт по ПРАВУ audit.view (делегируется директору,
              у которого нет доступа к /admin/users — Р-C). Ведёт на свой лог, дальше — селектор. */}
          {user && user.permissions.includes('audit.view') && (
            <NavLink
              component={Link}
              to={`/people/${user.id}/log`}
              label="Журнал действий"
              description="Активность + кадровый аудит"
              leftSection={<IconHistory size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/people/')}
            />
          )}
          {/* Блок Б слой 6: единственный пункт меню, гейтящийся по ПРАВУ (rbac.manage),
              а не по роли — раздача прав в узком круге. Остальные пункты — по роли (как было). */}
          {user && user.permissions.includes('rbac.manage') && (
            <NavLink
              component={Link}
              to="/admin/rbac"
              label="Роли и доступы"
              description="Матрица прав, override, уровни"
              leftSection={<IconShieldLock size={18} stroke={1.6} />}
              active={location.pathname.startsWith('/admin/rbac')}
            />
          )}

          {visibleDepts.length > 0 && (
            <>
              <Divider my="xs" />
              <Text size="xs" c="dimmed" px="xs" tt="uppercase" fw={600} mb={4}>
                Направления
              </Text>
              <NavLink
                component={Link}
                to={sidebarBase}
                label="Все"
                leftSection={<IconCategory size={16} stroke={1.6} />}
                active={!currentDept}
              />
              {visibleDepts.map((d) => (
                <NavLink
                  key={d.id}
                  component={Link}
                  to={`${sidebarBase}?department=${d.id}`}
                  label={d.name}
                  leftSection={<IconCategory size={16} stroke={1.6} />}
                  active={currentDept === d.id}
                />
              ))}
            </>
          )}
        </AppShell.Section>
        <AppShell.Section>
          <Text size="xs" c="dimmed" p="xs">
            v0.1 · Этап 1 MVP
          </Text>
        </AppShell.Section>
      </AppShell.Navbar>

      <AppShell.Main>
        {/* T12 эпик P4 — onboarding-gate баннер для непривязанных, БЕЗ дисмисса.
            Сам скрывается на /me/telegram (anti-loop) и если уже linked / is_stub. */}
        <TgLinkBanner user={user} />
        {children}
      </AppShell.Main>
    </AppShell>
  )
}

function openTasksWord(n: number): string {
  const m10 = n % 10
  const m100 = n % 100
  if (m10 === 1 && m100 !== 11) return 'открытая задача'
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return 'открытые задачи'
  return 'открытых задач'
}

function roleLabel(role?: string): string {
  switch (role) {
    case 'admin': return 'Администратор'
    case 'director': return 'Директор'
    case 'head': return 'Руководитель'
    case 'account': return 'Аккаунт'
    case 'employee': return 'Сотрудник'
    case 'observer': return 'Наблюдатель'
    default: return ''
  }
}
